import type { AdminOrderDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem, SectionTitle, StatBox } from './InfoBlocks';
import { OrderPiiValue } from './OrderPiiValue';
import { formatCurrency, formatDateTime } from '../../../utils/format';

/**
 * Report-ref section of the Обзор tab (AUT-123). Surfaces the embedded
 * `dto.report` summary (reportNumber + ReportStatus, i18n-mapped with a raw
 * fallback). The report is `null` until the inspection starts, so an
 * explicit empty-state mirrors the payment / payout null handling.
 */
const ReportSection = ({ report }: { report: AdminOrderDetailDto['report'] }): ReactElement => {
  const { t } = useTranslation();
  if (!report) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('orders.detail.report.empty')}
      </Typography>
    );
  }
  return (
    <InfoGrid>
      <InfoItem label={t('orders.detail.report.number')}>
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {report.reportNumber}
        </Box>
      </InfoItem>
      <InfoItem label={t('orders.detail.report.status')}>
        {t(`orders.detail.report.statusValue.${report.status}`, {
          defaultValue: report.status,
        })}
      </InfoItem>
    </InfoGrid>
  );
};

/**
 * VIN cell of the vehicle section. The VIN is optional since AUT-145, so a
 * null value collapses to the shared empty-field placeholder instead of
 * rendering a blank monospace cell.
 */
const VehicleVin = ({ vin }: { vin: string | null }): ReactElement => {
  const { t } = useTranslation();
  if (!vin) {
    return <>{t('orders.detail.noValue')}</>;
  }
  return (
    <Box component="span" sx={{ fontFamily: 'monospace' }}>
      {vin}
    </Box>
  );
};

/**
 * Обзор tab of the order detail drawer (AUT-123). Four sections mirroring
 * the mockup: parties + service, vehicle + visit, report ref, and a money
 * stat row.
 *
 * Identity values (customer / inspector name + phone) render through
 * `<OrderPiiValue>` (the users `PiiCell` pattern) agreeing with
 * `dto.piiRevealed`. Money amounts are 2-dp strings → `Number()` →
 * `formatCurrency` (BYN). Inspector is `null` when unassigned.
 */
export const OverviewTab = ({ order }: { order: AdminOrderDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { vehicle, currency } = order;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <SectionTitle>{t('orders.detail.sections.parties')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('orders.detail.fields.customer')}>
            <OrderPiiValue value={order.customer.name} revealed={order.piiRevealed} />
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.inspector')}>
            {order.inspector ? (
              <OrderPiiValue value={order.inspector.name} revealed={order.piiRevealed} />
            ) : (
              t('orders.unassigned')
            )}
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.customerPhone')}>
            <OrderPiiValue value={order.customer.phone} revealed={order.piiRevealed} />
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.inspectorPhone')}>
            {order.inspector ? (
              <OrderPiiValue value={order.inspector.phone} revealed={order.piiRevealed} />
            ) : (
              t('orders.detail.noValue')
            )}
          </InfoItem>
        </InfoGrid>
      </Box>

      <Box>
        <SectionTitle>{t('orders.detail.sections.vehicle')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('orders.detail.fields.vehicle')}>
            {`${vehicle.make} ${vehicle.model} · ${vehicle.year}`}
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.vin')}>
            <VehicleVin vin={vehicle.vin} />
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.scheduledAt')}>
            {formatDateTime(order.scheduledAt)}
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.createdAt')}>
            {formatDateTime(order.createdAt)}
          </InfoItem>
          <InfoItem label={t('orders.detail.fields.address')} span>
            {order.address}
          </InfoItem>
        </InfoGrid>
      </Box>

      <Box>
        <SectionTitle>{t('orders.detail.sections.report')}</SectionTitle>
        <ReportSection report={order.report} />
      </Box>

      <Box>
        <SectionTitle>{t('orders.detail.sections.money')}</SectionTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
          <StatBox
            value={formatCurrency(Number(order.total), currency)}
            label={t('orders.detail.money.total')}
          />
          <StatBox
            value={formatCurrency(Number(order.subtotal), currency)}
            label={t('orders.detail.money.subtotal')}
          />
          <StatBox
            value={formatCurrency(Number(order.platformFee), currency)}
            label={t('orders.detail.money.platformFee')}
          />
          <StatBox
            value={formatCurrency(Number(order.travelFee), currency)}
            label={t('orders.detail.money.travelFee')}
          />
        </Box>
      </Box>
    </Box>
  );
};
